import React from 'react';
import { useLocation } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import Navbar from '@/components/layout/Navbar';
import { MobileBottomNav } from '@/components/layout/MobileBottomNav';
import { FullPageSkeleton } from '@/components/ui/unified-skeleton';
import { useAccountSettings } from '@/context/AccountSettingsContext';
import { useAuth } from '@/context/AuthContext';
import { useBusinessContext } from '@/context/BusinessContext';
import {
  SidebarProvider,
  Sidebar,
  SidebarContent as SidebarUiContent,
  SidebarFooter as SidebarUiFooter,
  DashboardSidebarNavigation,
  SidebarInset,
  SidebarModeToggle,
  SidebarAIToggle
} from '@/components/ui/sidebar';
import { AIChatPanel, MobileAIChatOverlay } from '@/components/ai-chat';
import { MobileAIChatProvider } from '@/context/MobileAIChatContext';
import { AccountSettingsSidebarNav } from '@/components/settings/AccountSettingsSidebarNav';
import { AccountSettingsContent } from '@/components/settings/AccountSettingsContent';
import { BusinessSettingsSidebarNav } from '@/components/settings/BusinessSettingsSidebarNav';
import { BusinessSettingsContent } from '@/components/settings/BusinessSettingsContent';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useBusinessSettings } from '@/context/BusinessSettingsContext';
import { CommandPalette, DefaultCommands } from '@/components/command-palette';

// Logo icon component to match sidebar navigation pattern
const LogoIcon = () => (
  <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="size-6 shrink-0" />
);

// Determine dashboard type from pathname
type DashboardType = 'business' | 'employee' | 'employee-portal' | 'franchise' | null;

const getDashboardType = (pathname: string): DashboardType => {
  if (pathname.startsWith('/dashboard')) return 'business';
  if (pathname.startsWith('/employee-dashboard')) return 'employee';
  if (pathname.startsWith('/employee-portal')) return 'employee-portal';
  if (pathname.startsWith('/portfolio')) return 'franchise';
  return null; // Public page
};

interface GlobalLayoutProps {
  children: React.ReactNode;
}

export const GlobalLayout: React.FC<GlobalLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { isAccountSettingsOpen } = useAccountSettings();
  const { isBusinessSettingsOpen } = useBusinessSettings();
  const {
    businesses,
    loadingBusinesses,
    selectedBusinessId,
    switchBusiness,
  } = useBusinessContext();

  // Get dashboard tab context for navigation state
  const {
    businessDashboardTab,
    setBusinessDashboardTab,
    employeeDashboardTab,
    setEmployeeDashboardTab,
    employeePortalTab,
    setEmployeePortalTab,
    franchiseDashboardTab,
    setFranchiseDashboardTab
  } = useDashboardTab();

  const dashboardType = getDashboardType(location.pathname);
  const isOnDashboard = dashboardType !== null;

  // Track if we've completed initial auth to prevent skeleton flash on tab switch
  const hasInitializedRef = React.useRef(false);
  if (!authLoading && user) {
    hasInitializedRef.current = true;
  }

  // Show sidebar when: account/business settings is open OR user is on a dashboard page
  // Use ref to "remember" if we've shown sidebar to prevent layout flickering on tab switch
  const hasShownSidebarRef = React.useRef(false);
  const shouldShowSidebar = isAccountSettingsOpen || isBusinessSettingsOpen || (isOnDashboard && !!user);

  // Once we've shown the sidebar for a dashboard, keep showing it even if user temporarily becomes null
  if (shouldShowSidebar) {
    hasShownSidebarRef.current = true;
  }
  const showSidebar = shouldShowSidebar || (isOnDashboard && hasShownSidebarRef.current);

  // Show full page skeleton ONLY during initial auth loading, not on subsequent re-renders
  // This prevents the layout from unmounting/remounting on tab switch
  if (authLoading && isOnDashboard && !hasInitializedRef.current) {
    return <FullPageSkeleton />;
  }

  // Note: Business data comes from useBusinessContext() above
  // The context handles conditional fetching internally

  // Get the appropriate activeView and setActiveView based on dashboard type
  const getNavigation = () => {
    switch (dashboardType) {
      case 'business':
        return { activeView: businessDashboardTab, setActiveView: setBusinessDashboardTab };
      case 'employee':
        return { activeView: employeeDashboardTab, setActiveView: setEmployeeDashboardTab };
      case 'employee-portal':
        return { activeView: employeePortalTab, setActiveView: setEmployeePortalTab };
      case 'franchise':
        return { activeView: franchiseDashboardTab, setActiveView: setFranchiseDashboardTab };
      default:
        return { activeView: 'dashboard', setActiveView: () => {} };
    }
  };

  const { activeView, setActiveView } = getNavigation();

  // For pages without sidebar (public pages when settings is closed)
  if (!showSidebar) {
    return (
      <MobileAIChatProvider>
        {children}
        {/* Mobile bottom nav spacer - ensures content isn't hidden behind fixed nav */}
        <div className="h-24 md:hidden" />
        <MobileBottomNav />
        <MobileAIChatOverlay businessId={selectedBusinessId} />
      </MobileAIChatProvider>
    );
  }

  return (
    <MobileAIChatProvider>
      <SidebarProvider>
        {/* Command Palette - global keyboard shortcuts UI */}
        <CommandPalette />
        <DefaultCommands />
        <div className="w-full bg-gray-50 dark:bg-background flex">
          <Sidebar side="left" variant="sidebar" collapsible="icon">
            <SidebarUiContent className="p-0">
              {/* Logo section with static positioning */}
              <div className="pl-3 pt-2.5">
                <div className="flex items-center gap-2 h-8">
                  <LogoIcon />
                  <span className="text-sm font-medium text-sidebar-foreground group-data-[collapsible=icon]:opacity-0 transition-opacity duration-200">
                    Tag-Inn
                  </span>
                </div>
              </div>

              {/* Conditional Navigation - Priority: Account > Business > Dashboard */}
              {isAccountSettingsOpen ? (
                <AccountSettingsSidebarNav />
              ) : isBusinessSettingsOpen ? (
                <BusinessSettingsSidebarNav />
              ) : (
                dashboardType && selectedBusinessId && (
                  <DashboardSidebarNavigation
                    activeView={activeView}
                    setActiveView={setActiveView}
                    dashboardType={dashboardType}
                  />
                )
              )}
            </SidebarUiContent>

            {/* AI Chat Panel - only show when not in settings mode */}
            {!isAccountSettingsOpen && !isBusinessSettingsOpen && <AIChatPanel businessId={selectedBusinessId} />}

            <SidebarUiFooter className="flex-row items-center justify-center gap-1 group-data-[collapsible=icon]:flex-col">
              {!isAccountSettingsOpen && !isBusinessSettingsOpen && <SidebarAIToggle />}
              <SidebarModeToggle />
            </SidebarUiFooter>
          </Sidebar>

          <div className="flex flex-col flex-1" style={{ minWidth: 0 }}>
            <Navbar
              businesses={businesses}
              selectedBusinessId={selectedBusinessId}
              onBusinessChange={switchBusiness}
              loadingBusinesses={loadingBusinesses}
            />
            <SidebarInset className="flex-1">
              <ScrollArea className="flex-1 w-full">
                <div className="mx-auto p-4 w-full">
                  {isAccountSettingsOpen ? (
                    <AccountSettingsContent />
                  ) : isBusinessSettingsOpen ? (
                    <BusinessSettingsContent />
                  ) : (
                    children
                  )}
                </div>
                {/* Mobile bottom nav spacer - ensures content isn't hidden behind fixed nav */}
                <div className="h-24 md:hidden" />
              </ScrollArea>
            </SidebarInset>
          </div>
        </div>

        {/* Mobile Navigation Components */}
        <MobileBottomNav />
        <MobileAIChatOverlay businessId={selectedBusinessId} />
      </SidebarProvider>
    </MobileAIChatProvider>
  );
};

export default GlobalLayout;
